import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import Modal from "@/assets/common/Modal/Modal.vue";

interface Planting {
    id: string;
    name: string;
    crop: Crop;
    work_start_date: string | null;
    work_end_date: string | null;
}

interface AgriFieldPlanting {
    id: string;
    name: string;
    crop: {
      id: string;
      name: string;
    };
    cultivation_plan: {
        work_start: string | null;
        work_end: string | null;
    };
}

interface PlantingList {
    plantings: Array<Planting>;
}

interface Crop {
    id: string;
    name: string;
    phonetic: string;
}

interface DisplayItem {
    cropId: string;
    cropName: string;
    cropPhonetic: string;
    plantingId: string;
    plantingName: string;
    years: Array<string> | null;
    workingStart: string | null;
    workingEnd: string | null;
}

@Component({
    components: {Modal}
})
export default class SelectPlanting extends Vue {
    private plantings: PlantingList | [] = []
    private cropList: Array<Crop> = []
    private yearList: Array<string | null> = []
    private plantingList: Array<DisplayItem> = new Array<DisplayItem>()
    private selectedPlanting: DisplayItem | null = null
    private selectedPlantings: Array<DisplayItem> = []
    private allItems: Array<DisplayItem> = []
    private selectedCrop = ''
    private selectedYear = ''
    private searchKeyword = ''
    private plantingNameSortDesc = false
    private plantingTermSortDesc = true
    private currentSortKey = 'term'
    private selectedCropCategory: Array<string> = []
    private selectedYearCategory: Array<string> = []

    @Prop({ default: false })
    private multiple!: boolean

    @Prop({ default: false })
    private isSpl!: boolean

    @Prop({ default: ()=>[] })
    private preSelectPlantingId!: Array<string>

    @Prop({ default: false })
    private value!: boolean

    @Prop({ required: true })
    private selectedTenantId!: string;

    @Emit('input')
    private input(value: boolean) {
        value
    }

    created() {
        this.init()
    }

    @Watch('selectedTenantId')
    onTenantIdChanged(newVal: string) {
    this.init(); 
    }

    private init() {
        this.allItems = []
        this.selectedPlantings = []
        this.$apiClient.doRequest('get', `/plantings?tenant_id=${this.selectedTenantId}`).then((data)=>{
            const plantings = data.plantings
            this.plantings = plantings

            plantings.forEach((planting: Planting)=>{
                const item: DisplayItem = {
                    cropId: planting.crop.id,
                    cropName: planting.crop.name,
                    cropPhonetic: planting.crop.phonetic,
                    plantingId: planting.id,
                    plantingName: planting.name,
                    years: this.getYears(planting.work_start_date, planting.work_end_date),
                    workingStart: planting.work_start_date,
                    workingEnd: planting.work_end_date
                }
                this.allItems.push(item)
                this.initialSelect(item)
            })
            if (this.isSpl) {
                const item: DisplayItem = {
                    cropId: '作付未指定',
                    cropName: '作付未指定',
                    cropPhonetic: '作付未指定',
                    plantingId: '作付未指定',
                    plantingName: '作付未指定',
                    years: ["---"],
                    workingStart: null,
                    workingEnd: null
                }
                this.allItems.push(item)
                this.initialSelect(item)
            }
            this.search()
        })
    }

    // 初期選択
    initialSelect(item: DisplayItem) {
        this.selectedYear = 'すべて'
        if(this.preSelectPlantingId.length > 0) {
            if(this.preSelectPlantingId.indexOf(item.plantingId) > -1) {
                if(this.multiple) {
                    this.selectedPlantings.push(item)
                } else {
                    this.selectedPlanting = item
                }
            }
        }
    }

    selectCrop(cropId: string) {
        this.selectedCrop = cropId
        this.selectedYear = 'すべて'
        this.search()
    }

    selectYear(year: string) {
        this.selectedYear = year
        this.search()
    }

    search(): void {
        const filteredByKeyword = this.filterList(this.allItems, '', '', this.searchKeyword)
        this.cropList = this.createCropList(filteredByKeyword)
        const filteredByCrops = this.filterList(filteredByKeyword, this.selectedCrop, '', this.searchKeyword)
        this.yearList = this.createYearList(filteredByCrops)
        const filteredByYear = this.filterList(filteredByCrops, this.selectedCrop, this.selectedYear, this.searchKeyword)
        this.plantingList = this.createPlantingList(filteredByYear)
    }

    filterList(beforeArray: Array<DisplayItem>, cropId: string, year: string, searchKeyword: string): Array<DisplayItem>{
        let filteredItems: Array<DisplayItem> = []
        filteredItems = beforeArray.filter((item)=>{
            return (cropId == '' || cropId == item.cropId)
              && (year == '' || year == 'すべて' || item.years == null || item.years.indexOf(year) > -1)
              && (searchKeyword == '' || item.plantingName.indexOf(searchKeyword) > -1)
        })
        return filteredItems
    }

    batchSelect(event: Event, cropId: string, year: string) {
        const checked = ((event.target as any) as HTMLInputElement).checked

        const targetList: Array<DisplayItem> =  this.filterList(this.allItems, cropId, year, this.searchKeyword)
        let newList = []

        if(checked) {
            // チェック状態にしたときは追加
            newList = Array.from(new Set(targetList.concat(this.selectedPlantings)))
        } else {
            // チェック状態を外したときは削除
            newList = this.selectedPlantings.filter((item)=>{
                return !targetList.some((x)=> x.plantingId == item.plantingId)
            })
        }

        this.selectedPlantings = newList

    }

    createCropList(array: Array<DisplayItem>): Array<Crop> {
        let cropList = []
        const cropAll: Crop = {
            id: '',
            name: 'すべて',
            phonetic: ''
        }

        const cropIdList = array.map((item)=>item.cropId)
        cropList = array.filter((item, index)=>{
            return cropIdList.indexOf(item.cropId) === index
        })
        .map((item)=>{
            const crop = {
                id: item.cropId,
                name: item.cropName,
                phonetic: item.cropPhonetic
            }
            return crop
        })
        cropList = cropList.sort((a: Crop, b: Crop)=>this.compare(a.phonetic, b.phonetic))
        cropList.unshift(cropAll)
        return cropList
    }

    createYearList(array: Array<DisplayItem>): Array<string> {
        let yearList = []
        let allterm = false
        let maxYear = 0
        let minYear = 0
        let yearSet: Set<string> = new Set()
        let allTermCount = 0

        array.forEach((item: DisplayItem)=>{
            if (item.years != null) {
                item.years.forEach((year)=>{
                    const yearNum = Number(year)
                    if (maxYear == 0 || maxYear <= yearNum) {
                        maxYear = yearNum
                    }
                    if (minYear == 0 || minYear >= yearNum) {
                        minYear = yearNum
                    }
                    yearSet.add(year)
                })
            } else {
                allterm = true
                // 期間指定なしの件数
                allTermCount++
            }
        })

        // 期間指定なしが含まれているときは最小年～最大年までの年を補間する
        if (allterm) {
            yearSet = new Set()
            for (let i=minYear; i <= maxYear; i++) {
                yearSet.add(String(i))
            }
        }

        yearList = allTermCount !== array.length
            ? Array.from(yearSet).sort((a, b) =>this.compare(Number(a), Number(b), true))
            : []
        yearList.push('すべて')
        return yearList
    }

    createPlantingList(array: Array<DisplayItem>): Array<DisplayItem> {
        this.plantingList = []
        return array.sort((a: DisplayItem, b: DisplayItem) => a.plantingId === "作付未指定" ? 1 : b.plantingId === "作付未指定" ? -1 : this.compare(a.plantingName, b.plantingName));
    }

    sortPlantings(key: string) {
        if(key == 'name') {
            this.plantingNameSortDesc = !this.plantingNameSortDesc
            this.plantingList = this.plantingList.sort((a: DisplayItem, b: DisplayItem) => a.plantingId === "作付未指定" ? 1 : b.plantingId === "作付未指定" ? -1 : this.compare(a.plantingName, b.plantingName, this.plantingNameSortDesc))
        } else {
            this.plantingTermSortDesc = !this.plantingTermSortDesc
            this.plantingList = this.plantingList.sort((a: DisplayItem, b: DisplayItem) => a.plantingId === "作付未指定" ? 1 : b.plantingId === "作付未指定" ? -1 : this.compare(a.workingStart, b.workingStart, this.plantingTermSortDesc))
        }
        this.currentSortKey = key
    }

    compare(a: string | number | null, b: string | number | null, reverse?: boolean) {
        let comparison = 0;
        if(a == null) {
            return -1
        } else if(b == null) {
            return 1
        }

        if (a > b) {
            comparison = 1;
        } else if (a < b) {
            comparison = -1;
        }

        if (reverse == true) {
            // 降順ソート
            comparison = comparison * -1
        }

        return comparison;
    }

    getYears(workingStart: string | null, workingEnd: string | null): Array<string> | null {
        const array = []
        if(workingStart != null && workingEnd != null) {
            const start = Number(workingStart.substr(0, 4))
            const end = Number(workingEnd.substr(0, 4))

            for(let i=start; i <= end; i++ ) {
                array.push(String(i))
            }
            return array
        } else {
            return null
        }
    }

    isCategoryBold(cropId: string, year: string): boolean {
        if(this.selectedPlanting == null) {
            // 未選択時
            return false
        } else {
            return this.includeCheck(this.selectedPlanting, cropId, year)
        }
    }

    includeCheck(planthing: DisplayItem, cropId: string, year: string): boolean {
        if(cropId == '' && year == '') {
            // 作目："すべて"
            return true
        } else if(cropId != '') {
            // 作目の判定
            return planthing.cropId == cropId
        } else if(planthing.years == null){
            // 年の判定 期間指定なしの場合すべて対象
            return true
        } else if(year == 'すべて') {
            // 年："すべて"
            return true
        } else if(year != '') {
            // 年の判定
            return planthing.years.indexOf(year) > -1
        } else {
            return false
        }
    }

    isSelectedItem() {
        if (!this.multiple) {
            return this.selectedPlanting != null
        } else {
            return true
        }
    }

    createPlantingsData(displayItems: Array<DisplayItem>): Array<AgriFieldPlanting> {
        const plantingList: Array<AgriFieldPlanting> = displayItems.map((item)=>{
            return this.createPlantingData(item)
        })
        return plantingList
    }

    createPlantingData(displayItem: DisplayItem): AgriFieldPlanting {
        const planting: AgriFieldPlanting = {
            id: displayItem.plantingId,
            name: displayItem.plantingName,
            crop: {
                id: displayItem.cropId,
                name: displayItem.cropName
            },
            cultivation_plan: {
                work_start: displayItem.workingStart,
                work_end: displayItem.workingEnd
            }
        }
        return planting
    }

    @Emit('closeSelectPlantingModal')
    closemodal() {
        this.showSelectPlanting = false
    }

    @Emit('catchPlanting')
    sendPlanting() {
        if (this.multiple) {
            // 複数選択
            if(this.selectedPlantings.length > 0){
                const sendObj = this.createPlantingsData(this.selectedPlantings)
                this.closemodal()
                return sendObj
            } else {
                this.closemodal()
                return []
            }
        } else {
            // 単一選択
            if(this.selectedPlanting != null){
                const sendObj = this.createPlantingData(this.selectedPlanting)
                this.closemodal()
                return sendObj
            }
        }
    }

    @Watch('value')
    private async whenOpen(): Promise<void> {
        // 初期化
        this.selectedPlanting = null
        this.selectedPlantings = []
        this.selectedCropCategory = []
        this.selectedYearCategory = []
        this.selectedCrop = ''
        this.selectedYear = ''
        this.searchKeyword = ''

        // 検索状態をリセット
        this.search()

        // 初期選択
        this.allItems.forEach(item=>this.initialSelect(item))
    }

    @Watch('selectedPlantings')
    onUpdateCategoryChecked() {
        if(this.selectedPlantings.length > 0) {
            this.selectedCropCategory = this.selectedPlantings.map((item)=>item.cropId)
            this.selectedCropCategory.push('')
            this.selectedYearCategory = this.createYearList(this.selectedPlantings)
        } else {
            this.selectedCropCategory = []
            this.selectedYearCategory = []
        }
    }

  get showSelectPlanting(): boolean {
    return this.value
  }
  set showSelectPlanting(value: boolean) {
    this.input(value)
  }
}
